import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Clock, MapPin, Phone, Truck, Users, Warehouse } from 'lucide-react';
import { JobStatusBadge, LumperName, PageHead } from '../components/bits.jsx';
import { Bars } from '../components/charts.jsx';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  KeyValue,
  Loading,
  Stat,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { compactMoney, dateLabel, money, num, phone, phoneDial, timeLabel } from '../lib/format.js';

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = useApi(`/sites/${id}/detail`, { deps: [id] });
  const { data: coverage } = useApi(`/sites/${id}/coverage`, { deps: [id] });
  const [locating, setLocating] = useState(false);

  const findOnMap = async () => {
    setLocating(true);
    try {
      const result = await api.post(`/sites/${id}/locate`);
      if (result.data?.placed) {
        if (result.data.rough) toast.info('Roughly placed', result.data.note);
        else toast.ok('Placed on the map', result.data.note);
      } else {
        toast.err('We could not find that address', 'Check the street, city and ZIP, then save the facility again.');
      }
      refresh();
    } catch (err) {
      toast.err('Could not look the address up', err.message);
    } finally {
      setLocating(false);
    }
  };

  if (loading && !data) {
    return (
      <>
        <PageHead title="Facility" subtitle="Loading…" />
        <Loading rows={6} />
      </>
    );
  }
  if (!data?.site) {
    return <Card><EmptyState icon={Warehouse} title="Facility not found" /></Card>;
  }

  const { site, customer, jobs, dockActivity } = data;
  const open = jobs.filter((j) => ['posted', 'assigned', 'en_route', 'checked_in', 'in_progress'].includes(j.status));
  const completed = jobs.filter((j) => ['completed', 'verified', 'paid', 'invoiced'].includes(j.status));

  return (
    <>
      <PageHead
        breadcrumb={[
          { label: 'Facilities', to: '/app/sites' },
          ...(customer ? [{ label: customer.name, to: `/app/customers/${customer.id}` }] : []),
          { label: site.name },
        ]}
        title={site.name}
        subtitle={
          <span className="row gap-10 wrap">
            <span className="dim">{site.address}, {site.city}, {site.state} {site.zip}</span>
            {site.appointmentRequired ? <Badge tone="amber">Appointment required</Badge> : <Badge tone="muted">Drop in</Badge>}
            <span className="dim mono">{site.code}</span>
          </span>
        }
        actions={
          <>
            {site.contactPhone ? (
              <a href={`tel:${phoneDial(site.contactPhone)}`}><Button variant="outline" size="sm" icon={Phone}>Call dock</Button></a>
            ) : null}
            <Button variant="primary" size="sm" icon={Truck} onClick={() => navigate('/app/jobs?new=1')}>
              Post work here
            </Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Open work" icon={Clock} value={num(open.length)} tone={open.length ? 'amber' : 'lime'} foot="posted or in flight" />
        <Stat label="Jobs completed" icon={Truck} value={num(completed.length)} tone="blue" foot={`${num(site.totalJobs)} all-time`} />
        <Stat label="Avg dock wait" icon={Clock} value={Math.round(Number(site.avgWait || 0))} unit="min" tone={Number(site.avgWait) > 45 ? 'red' : 'amber'} foot="crew reported" />
        <Stat label="Revenue · 30d" icon={Warehouse} value={compactMoney(site.revenue30d)} tone="green" foot={`${site.dockDoors} dock doors`} />
      </div>

      <div className="split-wide mb-20">
        <Card>
          <CardHead title="Upcoming and in-flight" subtitle="What is happening at this dock" icon={Truck} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th className="narrow">Window</th>
                  <th className="narrow">Status</th>
                  <th>Lumper</th>
                  <th className="narrow">Door</th>
                  <th className="right">Pallets</th>
                  <th className="right">Pay</th>
                </tr>
              </thead>
              <tbody>
                {open.map((job) => (
                  <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                    <td className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{job.code}</td>
                    <td className="narrow num">{dateLabel(job.windowStart)} {timeLabel(job.windowStart)}</td>
                    <td className="narrow"><JobStatusBadge status={job.status} size="sm" /></td>
                    <td>
                      {job.lumperName ? (
                        <LumperName id={job.lumperId} name={job.lumperName} tone={job.lumperPhotoColor} />
                      ) : (
                        <Badge tone="amber" size="sm" dot>Unfilled</Badge>
                      )}
                    </td>
                    <td className="narrow num">{job.dockDoor || '—'}</td>
                    <td className="right num">{job.palletsExpected}</td>
                    <td className="right num">{money(job.lumperPay)}</td>
                  </tr>
                ))}
                {!open.length ? <tr><td colSpan={7}><EmptyState icon={Truck} title="Nothing scheduled here" /></td></tr> : null}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="col gap-20">
          <Card>
            <CardHead title="Dock rules" icon={Warehouse} />
            <CardBody>
              <KeyValue label="Dock hours">{site.dockHours}</KeyValue>
              <KeyValue label="Dock doors">{site.dockDoors}</KeyValue>
              <KeyValue label="Appointment">{site.appointmentRequired ? 'Required' : 'Drop in allowed'}</KeyValue>
              <KeyValue label="Lumpers required">{site.lumperRequired ? 'Yes' : 'Optional'}</KeyValue>
              <KeyValue label="Check-in geofence">{num(site.geofenceFeet)} ft</KeyValue>
              <div className="divider" />
              <span className="label">On the lumper map</span>
              {site.lat && site.lng ? (
                <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>
                  {site.geoNote || 'Placed from the address'} · {Number(site.lat).toFixed(4)}, {Number(site.lng).toFixed(4)}
                </p>
              ) : (
                <>
                  <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>
                    {site.geoNote || 'Not on the map yet — crews cannot see this dock or check in by phone from here.'}
                  </p>
                  <Button size="sm" variant="outline" icon={MapPin} loading={locating} onClick={findOnMap}>
                    Find it on the map
                  </Button>
                </>
              )}
              <div className="divider" />
              {site.checkInInstructions ? (
                <>
                  <span className="label">Check-in instructions</span>
                  <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{site.checkInInstructions}</p>
                </>
              ) : null}
              {site.parkingNotes ? (
                <>
                  <div className="divider" />
                  <span className="label">Parking / staging</span>
                  <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{site.parkingNotes}</p>
                </>
              ) : null}
              {site.contactName ? (
                <>
                  <div className="divider" />
                  <KeyValue label="Site contact">{site.contactName}</KeyValue>
                  <KeyValue label="Phone"><span className="mono">{phone(site.contactPhone)}</span></KeyValue>
                </>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Busiest hours" subtitle="Work orders by hour of day" icon={Clock} />
            <CardBody>
              <Bars
                data={(dockActivity || []).map((row) => ({ label: `${row.hour}:00`, value: row.jobs }))}
                height={140}
                tone="blue"
              />
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="split">
        <Card>
          <CardHead title="Job history" subtitle={`${jobs.length} work orders at this facility`} icon={Truck} />
          <div className="table-wrap" style={{ maxHeight: 420 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th className="narrow">Date</th>
                  <th className="narrow">Status</th>
                  <th>Lumper</th>
                  <th className="right">Pallets</th>
                  <th className="right">Wait</th>
                  <th className="right">Bill</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                    <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{job.code}</td>
                    <td className="narrow num">{dateLabel(job.windowStart)}</td>
                    <td className="narrow"><JobStatusBadge status={job.status} size="sm" dot={false} /></td>
                    <td className="truncate dim">{job.lumperName || '—'}</td>
                    <td className="right num">{job.palletsActual || job.palletsExpected}</td>
                    <td className="right num">{job.waitMinutes ? `${job.waitMinutes}m` : '—'}</td>
                    <td className="right num">{money(job.customerBill)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <CardHead
            title="Coverage nearby"
            subtitle="Lumpers close enough to serve this dock"
            icon={Users}
            actions={<Link to="/app/dispatch"><Button size="sm" variant="ghost">Dispatch</Button></Link>}
          />
          <CardBody className="col gap-10" style={{ maxHeight: 420, overflow: 'auto' }}>
            {(coverage || []).map((lumper) => (
              <div key={lumper.id} className={cx('candidate')} style={{ cursor: 'default' }}>
                <Avatar name={`${lumper.firstName} ${lumper.lastName}`} tone={lumper.photoColor || 'amber'} size="sm" />
                <div className="grow truncate">
                  <Link to={`/app/lumpers/${lumper.id}`} className="truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                    {lumper.firstName} {lumper.lastName}
                  </Link>
                  <span className="reason truncate">
                    {lumper.city} · {Number(lumper.rating || 0).toFixed(1)}★ · {lumper.openJobs} open
                  </span>
                </div>
                <Badge tone={Number(lumper.distanceMiles) <= 15 ? 'green' : 'muted'} size="sm">
                  {Number(lumper.distanceMiles).toFixed(1)} mi
                </Badge>
              </div>
            ))}
            {!coverage?.length ? <span className="dim">No active lumpers within 40 miles.</span> : null}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
